<template>
  <v-container
    fluid
    class="px-12 py-4 och-securityOrgRole"
    v-if="initialLoadComplete"
  >
    <v-row>
      <v-col cols="12">
        <breadcrumbs></breadcrumbs>
        <h1>{{ id ? "Edit" : "Create" }} Security Role</h1>
      </v-col>
    </v-row>
    <v-divider class="mb-4"></v-divider>
    <v-row v-if="id">
      <v-col cols="9">
        <div class="title">{{ roleRecord.name }}</div>
        <div>(Primary Role: {{ primaryRoleName }})</div>
      </v-col>
      <v-col cols="3" class="text-right">
        <v-btn
          :disabled="buildIsClosed"
          data-cy="editButton"
          class="mt-3"
          color="primary"
          outlined
          @click="edit"
          v-if="!showForm"
          >Edit</v-btn
        >
      </v-col>
    </v-row>
    <div v-if="showForm">
      <v-card outlined class="mt-5">
        <v-card-text>
          <v-stepper class="elevation-0" vertical v-model="stepper">
            <v-stepper-step :complete="stepper > 1" step="1">
              <span v-if="id">Review or Update Primary Role</span>
              <span v-else>Select a Role</span>
            </v-stepper-step>

            <v-stepper-content step="1">
              <v-form
                @submit.prevent="onSubmit"
                class="mt-5"
                v-model="validStep1"
              >
                <v-row>
                  <v-col class="col-12 col-md-9 col-lg-6 mb-2 pt-0">
                    <h3>Primary Role</h3>
                    <p class="bodyText--text">
                      Select a preconfigured role that best fits your
                      organization’s role. A description will appear upon
                      selection.
                    </p>
                    <v-autocomplete
                      @change="onPrimaryRoleChange"
                      :items="primaryRoleList"
                      :rules="ruleList"
                      :search-input.sync="search"
                      class="mt-1"
                      clearable
                      color="primary"
                      hide-details
                      item-text="name"
                      item-value="id"
                      label
                      outlined
                      v-model="primaryRoleId"
                    ></v-autocomplete>
                    <div v-if="primaryRoleId" class="my-5">
                      <h3>Primary Role Description</h3>
                      <p class="mb-3">{{ primaryRoleDescription }}</p>
                    </div>
                    <div
                      v-if="
                        primaryRoleId && !primaryRoleQuestionJoinList.length
                      "
                      class="my-9"
                    >
                      <p class="och-label mb-3">
                        There are no Security Questions associated with this
                        role.
                      </p>
                    </div>
                    <v-divider class="mt-2 mb-5"></v-divider>
                    <div class="text-right">
                      <v-btn
                        @click="resetForm"
                        class="mr-2 mt-1"
                        color="grey darken-2"
                        outlined
                        >Cancel</v-btn
                      >
                      <v-btn
                        @click="stepper = 2"
                        :disabled="!validStep1"
                        class="mr-2 mt-1 elevation-0"
                        color="primary"
                        >Continue</v-btn
                      >
                    </div>
                  </v-col>
                </v-row>
              </v-form>
            </v-stepper-content>

            <v-stepper-step
              :complete="stepper > 2"
              step="2"
              v-if="showSecurityQuestionStep"
            >
              <span v-if="id">Review or Update Security Questions</span>
              <span v-else>Answer Security Questions</span>
            </v-stepper-step>

            <v-stepper-content step="2" v-if="showSecurityQuestionStep">
              <v-form
                @submit.prevent="onSubmit"
                class="mt-5"
                v-model="validStep2"
              >
                <v-row>
                  <v-col class="col-12 col-md-12 col-lg-9 mb-2 pt-0">
                    <div
                      v-for="(item, index) in primaryRoleQuestionJoinList"
                      :key="index"
                    >
                      <question-answer
                        :answerMap="answerMap"
                        :depth="0"
                        :question="item"
                      ></question-answer>
                    </div>
                    <v-divider class="my-8"></v-divider>
                    <div class="d-flex">
                      <v-btn
                        @click="stepper = 1"
                        class="mr-2"
                        color="grey darken-2"
                        outlined
                      >
                        <v-icon class="mr-2" color="grey darken-2">
                          fas fa-caret-left</v-icon
                        >
                        Back to step {{ stepper - 1 }}</v-btn
                      >
                      <v-spacer></v-spacer>
                      <v-btn
                        @click="resetForm"
                        class="mr-2"
                        color="grey darken-2"
                        outlined
                        >Cancel</v-btn
                      >
                      <v-btn
                        @click="stepper = 3"
                        :disabled="!validStep2"
                        class="mr-2 elevation-0"
                        color="primary"
                        >Continue</v-btn
                      >
                    </div>
                  </v-col>
                </v-row>
              </v-form>
            </v-stepper-content>

            <v-stepper-step
              :complete="stepper > 3"
              :step="showSecurityQuestionStep ? 3 : 2"
            >
              <span v-if="id">Edit or Confirm Role Name</span>
              <span v-else>Create a Name for the Role</span>
            </v-stepper-step>

            <v-stepper-content :step="showSecurityQuestionStep ? 3 : 2">
              <v-form
                @submit.prevent="onSubmit"
                class="mt-5"
                v-model="validStep3"
              >
                <v-row>
                  <v-col class="col-12 col-md-9 col-lg-6 pt-0">
                    <label class="och-label">Role Name</label>
                    <v-text-field
                      :rules="ruleListOrganizationRoleName"
                      outlined
                      v-model="name"
                    ></v-text-field>
                    <label class="och-label">Comments</label>
                    <v-textarea
                      clearable
                      counter="255"
                      outlined
                      v-model="comments"
                    ></v-textarea>
                    <v-divider class="mt-5 mb-8"></v-divider>
                    <div class="d-flex">
                      <v-btn
                        @click="stepper = 2"
                        class="mr-2"
                        color="grey darken-2"
                        outlined
                      >
                        <v-icon class="mr-2" color="grey darken-2">
                          fas fa-caret-left</v-icon
                        >
                        Back to step {{ stepper - 1 }}</v-btn
                      >
                      <v-spacer></v-spacer>
                      <v-btn
                        @click="resetForm"
                        class="mr-2"
                        color="grey darken-2"
                        outlined
                        >Cancel</v-btn
                      >
                      <v-btn
                        :disabled="!validStep3 || saving"
                        :loading="saving"
                        @click="saveEdit"
                        class="elevation-0"
                        color="primary"
                        v-if="id"
                        >Save</v-btn
                      >
                      <v-btn
                        :disabled="!validStep3 || saving"
                        :loading="saving"
                        @click="saveCreate"
                        class="elevation-0"
                        color="primary"
                        v-else
                        >Save</v-btn
                      >
                    </div>
                  </v-col>
                </v-row>
              </v-form>
            </v-stepper-content>
          </v-stepper>
        </v-card-text>
      </v-card>
    </div>

    <div v-else>
      <v-divider class="my-5"></v-divider>
      <h2>Security Questions and Answers</h2>
      <div v-if="questionAnswerList.length">
        <v-row
          v-for="(item, index) in questionAnswerList"
          :key="index"
          class="mt-5"
        >
          <v-col cols="6"
            ><v-alert
              border="left"
              class="px-10 my-0"
              colored-border
              color="accent"
              >{{ item.question }}</v-alert
            ></v-col
          >
          <v-col cols="6"
            ><v-alert
              border="left"
              class="px-10 my-0"
              colored-border
              color="secondary"
              >{{ item.answer }}</v-alert
            ></v-col
          >
        </v-row>
      </div>
      <div v-else>
        There are no security questions associated with this role.
      </div>
      <h2 class="mt-6">Comments</h2>
      <v-row>
        <v-col sm="12" lg="6">
          <div v-if="roleRecord.comments">
            {{ roleRecord.comments }}
          </div>
          <div class="font-italic" v-else>
            There are no comments for this security role
          </div>
        </v-col>
      </v-row>
      <div v-if="snapshotCreated" class="caption mt-5 font-italic ml-3">
        <v-icon small class="mr-2">fas fa-info-circle</v-icon>
        Data snapshotted on {{ snapshotCreated }}
      </div>
    </div>

    <div v-show="id && isOchinUser && !showForm">
      <v-divider class="my-5"></v-divider>
      <h2>Security Template Assignment</h2>
      <role-template-form
        :roleId="id"
        ref="roleTemplateForm"
      ></role-template-form>
    </div>
    <snapshot ref="snapshot"></snapshot>
  </v-container>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { forEach, fromPairs, get, isEmpty, map } from "lodash";
import moment from "moment";

import Breadcrumbs from "@/views/Breadcrumbs";
import QuestionAnswer from "./QuestionAnswer";
import RoleTemplateForm from "./RoleTemplateForm.vue";
import Snapshot from "./Snapshot";

export default {
  components: {
    Breadcrumbs,
    QuestionAnswer,
    RoleTemplateForm,
    Snapshot,
  },
  computed: {
    answerMapIsComplete() {
      if (isEmpty(this.answerMap)) {
        return false;
      }

      if (
        Object.entries(this.answerMap).length <
        this.primaryRoleQuestionJoinList.length
      ) {
        return false;
      }
      return true;
    },
    buildIsClosed() {
      return this.organizationStatusIsGreaterThanOrEqualTo("buildClosed");
    },
    id() {
      return parseInt(this.$route.params.id, 10);
    },
    isOchinUser() {
      const { isOchinUser } = this.user;
      return isOchinUser;
    },
    primaryRoleDescription() {
      if (!this.primaryRoleId) {
        return;
      }
      const primaryRole = this.getPrimaryRoleById(this.primaryRoleId);
      return primaryRole.description;
    },
    primaryRoleName() {
      if (!this.primaryRoleId) {
        return;
      }
      const primaryRole = this.getPrimaryRoleById(this.primaryRoleId);
      return primaryRole.name;
    },
    primaryRoleParentQuestionJoinList() {
      return this.primaryRoleQuestionJoinList.filter(
        item => !item.question.parentAnswerId
      );
    },
    primaryRoleQuestionJoinList() {
      const filteredList = this.filterPrimaryRoleQuestionByAttribute(
        "secRolePrimaryId",
        this.primaryRoleId,
        [{ entity: "secQuestion", attribute: "secQuestionId" }]
      );
      const filteredListWithoutDeprecatedQuestions = filteredList.filter(
        item => !item.secQuestion.isDeprecated
      );

      filteredListWithoutDeprecatedQuestions.sort((a, b) =>
        a.orderNumber > b.orderNumber ? 1 : -1
      );

      return filteredListWithoutDeprecatedQuestions;
    },
    questionAnswerList() {
      return get(this.roleRecord, "snapshot.questionAnswerMap") || [];
    },
    showSecurityQuestionStep() {
      if (this.primaryRoleId && isEmpty(this.primaryRoleQuestionJoinList)) {
        return false;
      }
      return true;
    },
    snapshotCreated() {
      const timestamp = get(this.roleRecord, "snapshot.created");
      if (!timestamp) return null;

      return moment(timestamp).format("dddd, MMMM Do YYYY, h:mm:ss a");
    },
    ...mapGetters({
      getAnswerById: "secAnswer/getById",
      getOrganizationRoleById: "secRoleOrganization/getById",
      getPrimaryRoleById: "secRolePrimary/getById",
      getQuestionById: "secQuestion/getById",
      filterAnswerByAttribute: "secAnswer/filterByAttributeValue",
      filterOrganizationRoleAnswerByAttribute:
        "secRoleOrganizationSecAnswer/filterByAttributeValue",
      filterPrimaryRoleQuestionByAttribute:
        "secRolePrimarySecQuestion/filterByAttributeValue",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      answerList: state => state.secAnswer.list,
      organizationId: state => state.session.user.organizationId,
      organizationRoleAnswerList: state =>
        state.secRoleOrganizationSecAnswer.list,
      organizationRoleList: state => state.secRoleOrganization.list,
      primaryRoleList: state => state.secRolePrimary.list,
      primaryRoleQuestionList: state => state.secRolePrimarySecQuestion.list,
      questionList: state => state.secQuestion.list,
      user: state => state.session.user,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.fetchAnswerList(),
        this.fetchOrganizationRoleAnswerList(),
        this.fetchOrganizationRoleList(),
        this.fetchPrimaryRoleList(),
        this.fetchPrimaryRoleQuestionList(),
        this.fetchQuestionList(),
      ]);
      this.initialLoadComplete = true;
    } catch (e) {
      console.error(e);
      this.notifyErrorLoad();
    }
    if (!this.id) {
      this.showForm = true;
    } else {
      this.refreshOrganizationRole();
    }
  },
  data() {
    return {
      answerMap: {},
      comments: null,
      initialLoadComplete: false,
      name: null,
      primaryRoleId: null,
      roleRecord: {},
      ruleList: [v => !!v || "Required field"],
      ruleListOrganizationRoleName: [
        v => !!v || "Name is required",
        () =>
          this.nameDoesNotConflict() ||
          "A unique name is required. (Another role for your organization already uses this name.)",
      ],
      saving: false,
      search: null,
      searchAnswer: null,
      selectedAnswerList: [],
      showForm: false,
      stepper: 1,
      validStep1: false,
      validStep2: false,
      validStep3: false,
    };
  },
  methods: {
    edit() {
      this.name = this.roleRecord.name ? `${this.roleRecord.name}` : null;
      this.showForm = true;
    },
    filterOrganizationRoleAnswerList(secRoleOrganizationId) {
      const filteredList = this.filterOrganizationRoleAnswerByAttribute(
        "secRoleOrganizationId",
        secRoleOrganizationId
      );
      return filteredList;
    },
    getAnswerFromAnswerMap(questionId) {
      const answerId = this.answerMap[questionId];
      const answer = this.getAnswerById(answerId);
      if (answer) return answer.text;
    },
    nameDoesNotConflict() {
      const name = this.name;

      if (!name) return true;

      if (typeof this.roleRecord.name === "string" && this.roleRecord.name.trim() === name.trim()) return true;

      if (this.organizationRoleList) {
        const conflict = this.organizationRoleList.find(role => {
          return (
            `${role.name}`.trim().toUpperCase() ===
            `${name}`.trim().toUpperCase()
          );
        });
        if (conflict) return false;
      }
      return true;
    },
    onPrimaryRoleChange() {
      this.answerMap = {};
    },
    refreshAnswerMap(organizationRoleId) {
      // Get the answers associated with the currently selected organization role
      const orgAnswerList = this.filterOrganizationRoleAnswerList(
        organizationRoleId
      );

      // Get the answer and question ids from the secAnswer table
      const answerMapArray = orgAnswerList.map(answer => {
        const secAnswer = this.getAnswerById(answer.secAnswerId);
        // secQuestionId is cast as String because it needs to become the key in the answerMaap
        const secQuestionId = String(secAnswer.secQuestionId);
        const secAnswerId = secAnswer.id;

        let questionAnswerPair = [secQuestionId, secAnswerId];

        if (secAnswer.isDeprecated) {
          questionAnswerPair = null;
        }
        return questionAnswerPair;
      });

      // Filter out the deprecated pairs that were set to null because the answer is deprecated
      const filteredAnswerMapArray = answerMapArray.filter(item => {
        return item !== null;
      });

      this.answerMap = fromPairs(filteredAnswerMapArray);
    },
    async refreshData() {
      await Promise.all([
        this.fetchOrganizationRoleList(),
        this.fetchOrganizationRoleAnswerList(),
      ]);

      this.refreshOrganizationRole();
    },
    refreshOrganizationRole() {
      const role = this.getOrganizationRoleById(this.id);

      // Check that role exists in the list, which is filtered by organizationId.
      if (role) {
        this.comments = role.comments || null;
        this.roleRecord = role;
        this.primaryRoleId = role.secRolePrimaryId;
        this.name = role.name || null;
        this.refreshAnswerMap(this.id);
        if (this.roleRecord.snapshot && typeof this.roleRecord.snapshot === 'string') {
          this.roleRecord.snapshot = JSON.parse(this.roleRecord.snapshot);
        }
        return;
      }

      // If role does not exist for this organization, return to list.
      this.$router.push({
        name: "organizationRoleList",
      });
    },
    resetForm() {
      this.showForm = false;
      this.stepper = 1;
      if (this.id) {
        this.refreshOrganizationRole();
      } else {
        this.answerMap = {};
        this.comments = null;
        this.name = null;
        this.primaryRoleId = null;
        this.$router.push({
          name: "organizationRoleList",
        });
      }
    },
    async saveCreate() {
      this.saving = true;
      try {
        const result = await this.createRole({
          comments: this.comments,
          name: this.name.trim(),
          organizationId: this.organizationId,
          secRolePrimaryId: this.primaryRoleId,
        });

        forEach(this.answerMap, async val => {
          await this.createRoleAnswer({
            secRoleOrganizationId: result.id,
            secAnswerId: val,
          });
        });

        await this.$refs.snapshot.createSnapshot({
          answerMap: this.answerMap,
          secRoleOrganizationId: result.id,
        });

        await this.refreshData(result.id);

        this.$notifySuccess({ message: `Successfully created ${this.name}` });

        this.resetForm();
        return;
      } catch (e) {
        console.error(e);
        this.$notifyError({ message: "Unable to save Security Role data" });
      } finally {
        this.saving = false;
      }
    },
    async saveEdit() {
      this.saving = true;
      try {
        await this.updateRole([
          this.id,
          {
            comments: this.comments,
            name: this.name.trim(),
            secRolePrimaryId: this.primaryRoleId,
          },
        ]);

        // Before saving new records, delete existing answer-role associations in table.
        // First, get the list of all associations for this organization role.
        const filteredRoleOrganizationAnswerList = this.filterOrganizationRoleAnswerList(
          this.id
        );
        // Delete all answer-role associations.
        for (const record of filteredRoleOrganizationAnswerList) {
          await this.deleteRoleAnswer(record.id);
        }

        // Iterate over answerMap object and create an array of promises.
        const createRoleAnswerPromiseList = map(this.answerMap, val => {
          return this.createRoleAnswer({
            secRoleOrganizationId: this.id,
            secAnswerId: val,
          });
        });

        try {
          await Promise.all(createRoleAnswerPromiseList);
        } catch (e) {
          console.error(e);
          this.notifyErrorCreateOrganizationRole();
        }

        await this.$refs.snapshot.createSnapshot({
          answerMap: this.answerMap,
          secRoleOrganizationId: this.id,
        });

        await this.refreshData(this.id);
        await this.$refs.roleTemplateForm.refreshRole();
        this.resetForm();
        this.$notifySuccess({ message: `Successfully edited ${this.name}` });
        return;
      } catch (e) {
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    ...mapActions({
      createRole: "secRoleOrganization/create",
      createRoleAnswer: "secRoleOrganizationSecAnswer/create",
      deleteRoleAnswer: "secRoleOrganizationSecAnswer/remove",
      getOrganizationRole: "secRoleOrganization/get",
      fetchAnswerList: "secAnswer/list",
      fetchOrganizationRoleAnswerList: "secRoleOrganizationSecAnswer/list",
      fetchOrganizationRoleList: "secRoleOrganization/list",
      fetchPrimaryRoleList: "secRolePrimary/list",
      fetchPrimaryRoleQuestionList: "secRolePrimarySecQuestion/list",
      fetchQuestionList: "secQuestion/list",
      updateRole: "secRoleOrganization/update",
    }),
  },
  notifications: {
    notifyErrorCreateOrganizationRole: {
      title: "Error",
      message: "Could not create Organization Role.",
      type: "warn",
    },
    notifyErrorLoad: {
      message: "Could not load initial page state. Refresh page and try again.",
      timeout: 0,
    },
  },
};
</script>

<style scoped>
.och-securityOrgRole h3 {
  color: var(--v-bodyText-base);
  font-size: 1rem;
}
</style>
